import { ComponentProps, createContext, PropsWithChildren, useContext } from 'react';
import clsx from 'clsx';
import styles from './grid.module.scss';
import { classNameMapper, Columns } from './grid.function';
import { capitalizeFirstLetter } from '../../utils/string-handling/capitalize-first-letter';

export type GapValues = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 12;

export interface BlockProps {
  xs?: Columns;
  sm?: Columns;
  md?: Columns;
  lg?: Columns;
  xl?: Columns;
  xsStart?: Columns;
  smStart?: Columns;
  mdStart?: Columns;
  lgStart?: Columns;
  xlStart?: Columns;
  xsEnd?: Columns;
  smEnd?: Columns;
  mdEnd?: Columns;
  lgEnd?: Columns;
  xlEnd?: Columns;
  justify?: 'start' | 'center' | 'end' | 'between';
  alignItems?: 'start' | 'center' | 'end';
}

interface FullBlockProps extends BlockProps, ComponentProps<'div'> {}

export const Block = ({ className, children, ...props }: PropsWithChildren<FullBlockProps>) => {
  const breakpoints = [props.xs, props.sm, props.md, props.lg, props.xl];
  const starts = [props.xsStart, props.smStart, props.mdStart, props.lgStart, props.xlStart];
  const ends = [props.xsEnd, props.smEnd, props.mdEnd, props.lgEnd, props.xlEnd];

  const justify = props.justify ?? '';
  const justifyKind = `justify${capitalizeFirstLetter(justify)}`;
  const justifyKindStyles = styles[justifyKind];

  const align = props.alignItems ?? '';
  const alignKind = `align${capitalizeFirstLetter(align)}`;
  const alignKindStyles = styles[alignKind];

  const classes = clsx([
    styles.block,
    ...breakpoints.map(classNameMapper(styles)),
    ...starts.map(classNameMapper(styles, 'Start')),
    ...ends.map(classNameMapper(styles, 'End')),
    { [styles.justify!]: !!props.justify, [justifyKindStyles!]: !!props.justify },
    { [styles.align!]: !!props.alignItems, [alignKindStyles!]: !!props.alignItems },
    className
  ]);

  return <div className={classes}>{children}</div>;
};

interface GridProps extends ComponentProps<'div'> {
  /**
   * Set to true if this grid is acting as a row in another grid, to span all 12 columns.
   */
  row?: boolean;
  /**
   * When `true` uses the dense auto-flow grid algorithm, to place grid items earlier if possible
   */
  dense?: boolean;
  /**
   * The gap between elements as a multiple of 8px. Defaults to 2
   */
  gap?: GapValues;
  /**
   * Override the column gap, values translate to a multiple of 8px on larger screens, and 4px for smaller screens.
   */
  colGap?: GapValues;
  /**
   * Override the row gap, values translate to a multiple of 8px on larger screens, and 4px for smaller screens.
   */
  rowGap?: GapValues;
}

export const Grid = ({
  children,
  className,
  row,
  dense,
  gap,
  colGap,
  rowGap,
  ...props
}: PropsWithChildren<GridProps>) => {
  const {
    gap: ctxGap,
    rowGap: ctxRowGap,
    colGap: ctxColGap
  } = useContext(GridGapContext) ?? { gap: 2 };

  const baseGap = gap ?? ctxGap;
  const gaps = {
    rowGap: rowGap ?? ctxRowGap ?? baseGap,
    colGap: colGap ?? ctxColGap ?? baseGap
  };

  const colGapStyle = styles[`colGap${gaps.colGap}`];
  const rowGapStyle = styles[`rowGap${gaps.rowGap}`];
  return (
    <div
      {...props}
      className={clsx([
        styles.grid,
        { [styles.gridRow!]: row },
        { [styles.dense!]: dense },
        colGapStyle,
        rowGapStyle,
        className
      ])}
    >
      {children}
    </div>
  );
};

type GridGapContextProps = Pick<GridProps, 'gap' | 'rowGap' | 'colGap'>;
const GridGapContext = createContext<GridGapContextProps | undefined>(undefined);
export const GridGapProvider = ({
  gap,
  rowGap,
  colGap,
  children
}: PropsWithChildren<Pick<GridProps, 'gap' | 'rowGap' | 'colGap'>>) => {
  return (
    <GridGapContext.Provider value={{ gap, rowGap, colGap }}>{children}</GridGapContext.Provider>
  );
};
